/**
 * PANTALLA 08: LO QUE VE JUSTO DESPUÉS DE PEDIR SU AJUSTE PERSONALIZADO
 * (documento y vídeo de Jesús del 7-09).
 *
 * Antes no existía. Se pagaba, el circuito creaba la propuesta y avisaba al equipo, y al
 * cliente no se le enseñaba nada ni se le pedía nada: salía de la pasarela a la pantalla de
 * bienvenida de siempre, sin saber que había un paso suyo esperándole.
 *
 * LOS TEXTOS SON SUYOS, dictados en el vídeo, y van tal cual:
 *
 *      7:15  «Te hemos mandado un aviso y lo tienes en Seguimiento.»
 *      7:21  «Nos adjuntas tus fotos, tus métricas y algunos datos más.»
 *      7:31  «Y desde que nos llegue, en un plazo máximo de 48 horas, te ponemos tus macros
 *             y tu suplementación a medida.»
 *      7:48  «Rellena el reporte ahora.»
 *      7:56  «Mientras tanto sigues con estos.»
 *
 * DOS COSAS QUE ÉL CORRIGE Y QUE AQUÍ SE RESPETAN. La maqueta decía «te hemos mandado un
 * reporte al correo» y en el vídeo se corrige a «un aviso y lo tienes en Seguimiento»: es la
 * buena, y además es la única que la app puede cumplir, porque los correos están apagados. Y
 * las 48 horas cuentan desde que llega el reporte, no desde el pago, que es lo que hacía la
 * app: con un paso suyo en medio, contar desde el pago era prometer algo imposible.
 *
 * LA TABLA DE «DE DÓNDE SALE» NO ESTÁ: la retiró en el vídeo (8:01, «olvidaros de aquí esa
 * tabla»).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ClipboardList, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { MACRO } from './ClientDashboard';
import { num1 } from '../lib/numeros';

const CAMPOS = [
    { key: 'proteina', label: 'Proteína', color: MACRO.protein },
    { key: 'hidratos', label: 'Hidratos', color: MACRO.carbs },
    { key: 'grasa', label: 'Grasas', color: MACRO.fat },
];

// Los macros vienen del perfil con los nombres del servidor (`macros_training`), que no son
// los de la pantalla. Un solo sitio donde se traduzcan.
const deLosMacros = (m) => (m ? {
    proteina: m.protein ?? m.proteina,
    hidratos: m.carbs ?? m.hidratos,
    grasa: m.fat ?? m.grasa,
} : null);

const AjustePersonalizadoPage = () => {
    const navigate = useNavigate();
    const { api, profile, refreshProfile } = useAuth();
    const [confirmando, setConfirmando] = useState(false);

    // LA VUELTA DE LA PASARELA, CONFIRMADA AQUÍ. Es la misma llamada que hacía la pantalla de
    // bienvenida, y tiene que seguir haciéndose desde donde caiga el cliente: en local no hay
    // webhook que lo salve, y en producción tampoco se puede depender solo de él.
    useEffect(() => {
        const sesion = new URLSearchParams(window.location.search).get('session_id');
        if (!sesion) return;
        setConfirmando(true);
        api.post('/billing/checkout-session/sync', { session_id: sesion })
            .then(() => refreshProfile && refreshProfile())
            .catch((e) => console.error('[pago] no se pudo confirmar la sesión', e))
            .finally(() => {
                setConfirmando(false);
                window.history.replaceState({}, '', window.location.pathname);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reporte = profile?.ajuste_a_medida?.reporte;
    const yaLoMando = reporte?.estado === 'recibido';
    const entreno = deLosMacros(profile?.macros_training);
    const descanso = deLosMacros(profile?.macros_rest);

    return (
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-5 animate-fade-in"
            data-testid="ajuste-personalizado-page">

            {confirmando && (
                <p className="surface p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Estamos confirmando tu pago…
                </p>
            )}

            <div className="flex items-start gap-3">
                <CheckCircle2 className="w-7 h-7 text-brand flex-none mt-1" />
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground leading-tight">
                        Ya lo tenemos
                    </h1>
                    {/* Su frase del 7:15, con la correccion del video: aviso y Seguimiento, no
                        correo. */}
                    <p className="text-sm text-muted-foreground mt-1">
                        Te hemos mandado un aviso y lo tienes en Seguimiento.
                    </p>
                </div>
            </div>

            {/* LOS DOS PASOS, POR ORDEN (regla 08·1): primero el reporte, y desde que llega,
                las 48 horas. El orden es la mitad de lo que pide: antes solo se decía «te
                contestamos en 48 horas», sin el paso suyo y contando desde el pago. */}
            <section className="surface p-5 space-y-4" data-testid="ajuste-los-dos-pasos">
                <div className="flex gap-3">
                    <span className="flex-none w-6 h-6 rounded-full bg-brand text-white text-xs font-bold grid place-items-center">1</span>
                    <p className="text-[15px] text-foreground leading-relaxed">
                        Nos adjuntas tus <strong>fotos</strong>, tus <strong>métricas</strong> y
                        algunos datos más.
                    </p>
                </div>
                <div className="flex gap-3">
                    <span className="flex-none w-6 h-6 rounded-full bg-brand text-white text-xs font-bold grid place-items-center">2</span>
                    <p className="text-[15px] text-foreground leading-relaxed">
                        Y desde que nos llegue, en un plazo máximo de{' '}
                        <strong>48 horas</strong>, te ponemos tus macros y tu suplementación a
                        medida.
                    </p>
                </div>

                {yaLoMando ? (
                    <p className="text-sm text-muted-foreground border-t border-border pt-4"
                        data-testid="ajuste-reporte-enviado">
                        Ya nos has mandado tu reporte. Estamos con él.
                    </p>
                ) : (
                    <button type="button" onClick={() => navigate('/dashboard/reports')}
                        data-testid="ajuste-rellena-reporte"
                        className="w-full sm:w-auto px-5 py-3 rounded-xl bg-brand text-white text-sm font-bold hover:opacity-90 inline-flex items-center justify-center gap-2">
                        <ClipboardList className="w-4 h-4" />
                        Rellena el reporte ahora
                    </button>
                )}
            </section>

            {/* «MIENTRAS TANTO SIGUES CON ESTOS» (regla 08·3, video 7:56). La pantalla no se
                queda en blanco esperando: hasta que lleguen los nuevos, los suyos son estos, y
                se le enseñan para que no tenga que ir a buscarlos. */}
            {(entreno || descanso) && (
                <section className="surface p-5 space-y-3" data-testid="ajuste-mientras-tanto">
                    <p className="caption">Mientras tanto sigues con estos</p>
                    {[['Día de entreno', entreno], ['Día de descanso', descanso]]
                        .filter(([, m]) => m)
                        .map(([nombre, m]) => (
                            <div key={nombre} className="flex items-baseline justify-between gap-3">
                                <span className="text-sm text-muted-foreground">{nombre}</span>
                                <span className="font-data text-lg font-bold text-foreground">
                                    {CAMPOS.map((c, i) => (
                                        <React.Fragment key={c.key}>
                                            {i > 0 && <span className="text-muted-foreground font-normal"> · </span>}
                                            {num1(m[c.key])}
                                        </React.Fragment>
                                    ))}
                                </span>
                            </div>
                        ))}
                    <button type="button" onClick={() => navigate('/dashboard/macro-calculator')}
                        className="text-xs font-semibold text-brand hover:underline inline-flex items-center gap-1">
                        Ver mis macros <ArrowRight className="w-3 h-3" />
                    </button>
                </section>
            )}
        </div>
    );
};

export default AjustePersonalizadoPage;
